<script lang="ts">
  import type { Snippet } from 'svelte';
  import { t } from '../i18n/index.svelte';
  import { drawToCanvas } from '../image';
  import type { RgbaData } from '../types';

  export type PreviewTab = { id: string; label: string; image: RgbaData | null };
  export interface Painter {
    down(x: number, y: number, e: PointerEvent): void;
    move(x: number, y: number): void;
    up(): void;
  }

  let {
    tabs,
    active = $bindable(),
    status,
    toolbar,
    busy = false,
    painter,
    brushRadius = 0,
  }: {
    tabs: PreviewTab[];
    active: string;
    status?: Snippet;
    toolbar?: Snippet;
    busy?: boolean;
    painter?: Painter;
    brushRadius?: number;
  } = $props();

  let stage: HTMLDivElement;
  let painting = false;
  let cursor = $state<{ x: number; y: number; r: number } | null>(null);

  function toImage(e: PointerEvent): [number, number] {
    const rect = canvas.getBoundingClientRect();
    return [
      ((e.clientX - rect.left) * canvas.width) / rect.width,
      ((e.clientY - rect.top) * canvas.height) / rect.height,
    ];
  }

  function onDown(e: PointerEvent) {
    if (!painter || (e.button !== 0 && e.button !== 2)) return;
    painting = true;
    canvas.setPointerCapture(e.pointerId);
    painter.down(...toImage(e), e);
  }

  function onMove(e: PointerEvent) {
    if (!painter) return;
    const rect = canvas.getBoundingClientRect();
    const srect = stage.getBoundingClientRect();
    cursor = {
      x: e.clientX - srect.left + stage.scrollLeft,
      y: e.clientY - srect.top + stage.scrollTop,
      r: (brushRadius * rect.width) / canvas.width,
    };
    if (painting) painter.move(...toImage(e));
  }

  function onUp() {
    if (!painting) return;
    painting = false;
    painter?.up();
  }

  let canvas: HTMLCanvasElement;
  let fit = $state(true);
  const current = $derived(tabs.find((x) => x.id === active) ?? tabs[0]);

  $effect(() => {
    const img = current?.image;
    if (canvas && img) drawToCanvas(canvas, img);
  });

  /** The canvas currently displayed (for PNG export). */
  export function getCanvas(): HTMLCanvasElement {
    return canvas;
  }
</script>

<div class="preview">
  <div class="bar">
    <div class="seg tabs">
      {#each tabs as tab (tab.id)}
        <button class:on={tab.id === active} onclick={() => (active = tab.id)} data-testid={`tab-${tab.id}`}
          >{tab.label}</button
        >
      {/each}
    </div>
    <div class="status">{@render status?.()}</div>
    <div class="row tools">
      <div class="seg">
        <button class:on={fit} onclick={() => (fit = true)}>{t('preview.fit')}</button>
        <button class:on={!fit} onclick={() => (fit = false)}>1:1</button>
      </div>
      {@render toolbar?.()}
    </div>
  </div>
  <div class="stage" class:fit class:busy bind:this={stage}>
    <canvas
      bind:this={canvas}
      data-testid="preview-canvas"
      class:hidden={!current?.image}
      class:paint={!!painter}
      onpointerdown={onDown}
      onpointermove={onMove}
      onpointerup={onUp}
      onpointercancel={onUp}
      onpointerleave={() => (cursor = null)}
      oncontextmenu={(e) => painter && e.preventDefault()}
    ></canvas>
    {#if painter && cursor}
      <div
        class="cursor"
        style:left={`${cursor.x - cursor.r}px`}
        style:top={`${cursor.y - cursor.r}px`}
        style:width={`${cursor.r * 2}px`}
        style:height={`${cursor.r * 2}px`}
      ></div>
    {/if}
    {#if !current?.image}
      <div class="empty muted">{busy ? t('preview.rendering') : t('preview.empty')}</div>
    {/if}
  </div>
</div>

<style>
  .preview {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--line);
    background: var(--panel);
    flex-wrap: wrap;
  }
  .tabs {
    min-width: 260px;
  }
  .status {
    flex: 1;
    font-size: 12px;
    min-width: 120px;
  }
  .stage {
    flex: 1;
    min-height: 0;
    overflow: auto;
    display: grid;
    place-items: center;
    padding: 16px;
    background: repeating-conic-gradient(#14161b 0% 25%, #111317 0% 50%) 50% / 24px 24px;
  }
  .stage.fit canvas {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .stage:not(.fit) {
    place-items: start;
  }
  canvas {
    image-rendering: auto;
    box-shadow: 0 4px 30px #0008;
    transition: opacity 0.2s;
  }
  .stage.busy canvas {
    opacity: 0.75;
  }
  .hidden {
    display: none;
  }
  .stage {
    position: relative;
  }
  canvas.paint {
    cursor: crosshair;
    touch-action: none;
  }
  .cursor {
    position: absolute;
    border: 1px solid var(--accent);
    border-radius: 50%;
    pointer-events: none;
    box-shadow: 0 0 0 1px #0008;
  }
  .empty {
    font-size: 14px;
  }
</style>
